import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Send, Loader2, Clock } from "lucide-react";
import { Reveal, SectionTag, SectionHeading } from "./Reveal";
import { CONTACT } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", company: "", email: "", phone: "", region: "United States", service: "Order Management", message: "" };

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-600 outline-none transition-colors duration-300 focus:border-[#6DC8F0]/60 focus:bg-white";

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Inquiry sent — our team will reach out shortly.");
      setForm(initial);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative overflow-hidden bg-[#F8FAFC] py-24 sm:py-32">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(900px circle at 15% 100%, rgba(46,49,146,0.10), transparent 65%)" }}
      />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-5">
            <SectionTag index="09" label="Contact" testId="contact-section-tag" />
            <div className="mt-6">
              <SectionHeading>Let's run your order desk, live.</SectionHeading>
            </div>
            <Reveal delay={0.15}>
              <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-500 sm:text-base">
                Tell us about your volume and workflow — we'll map a dedicated order
                management and design team to your business hours.
              </p>
            </Reveal>

            <Reveal delay={0.22}>
              <div className="mt-10 space-y-5">
                <a
                  href={`mailto:${CONTACT.email}`}
                  data-testid="contact-email-link"
                  className="flex items-center gap-3.5 text-sm text-slate-600 transition-colors hover:text-[#2E3192]"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#2E3192]/10 text-[#2E3192]">
                    <Mail className="h-4 w-4" />
                  </span>
                  {CONTACT.email}
                </a>
                <div className="flex items-start gap-3.5 text-sm text-slate-600">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#2E3192]/10 text-[#2E3192]">
                    <MapPin className="h-4 w-4" />
                  </span>
                  <span>
                    <span className="font-semibold text-slate-900">{CONTACT.company}</span>
                    <br />
                    <span className="text-slate-500">{CONTACT.address}</span>
                  </span>
                </div>
                <div className="flex items-start gap-3.5 text-sm text-slate-600">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#2E3192]/10 text-[#2E3192]">
                    <Clock className="h-4 w-4" />
                  </span>
                  <span className="text-slate-500">{CONTACT.hoursLine}</span>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.2}>
              <form
                onSubmit={submit}
                data-testid="contact-form-container"
                className="rounded-2xl border border-slate-200 bg-white/95 p-7 backdrop-blur sm:p-10"
              >
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <input
                    required
                    data-testid="contact-input-name"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Full name *"
                    className={inputCls}
                  />
                  <input
                    required
                    data-testid="contact-input-company"
                    value={form.company}
                    onChange={set("company")}
                    placeholder="Company *"
                    className={inputCls}
                  />
                  <input
                    required
                    type="email"
                    data-testid="contact-input-email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="Work email *"
                    className={inputCls}
                  />
                  <input
                    data-testid="contact-input-phone"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="Phone (optional)"
                    className={inputCls}
                  />
                  <select
                    data-testid="contact-select-region"
                    value={form.region}
                    onChange={set("region")}
                    className={`${inputCls} appearance-none`}
                  >
                    {["United States", "Canada", "United Kingdom", "Australia", "Other"].map((r) => (
                      <option key={r} className="bg-white">
                        {r}
                      </option>
                    ))}
                  </select>
                  <select
                    data-testid="contact-select-service"
                    value={form.service}
                    onChange={set("service")}
                    className={`${inputCls} appearance-none`}
                  >
                    {["Order Management", "Graphic Designing", "Both"].map((s) => (
                      <option key={s} className="bg-white">
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <textarea
                  required
                  rows={5}
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Tell us about your order volume, decoration needs, or workflow... *"
                  className={`${inputCls} mt-5 resize-none`}
                />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="group mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-[#2E3192] py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#6DC8F0] hover:text-[#07090E] disabled:opacity-60 sm:w-auto sm:px-10"
                >
                  {sending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                  )}
                  {sending ? "Sending..." : "Send Inquiry"}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
